"use client";

import { useEffect, useMemo, useRef } from "react";
import { safeFrame, sanitizeArticleContent, socialEmbedUrl } from "@/lib/articles/richContent";
import { useConsent } from "@/components/privacy/ConsentRuntime";
import { customCodeAllowed } from "@/lib/privacy/consent";

import { buildPublicationUrl } from "@/lib/publications/urlBuilder";
import "./ArticleBody.css";

const scripts = new Map();
function loadScript(src) {
  if (!scripts.has(src)) scripts.set(src, new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { scripts.delete(src); script.remove(); reject(new Error("Embed unavailable")); };
    document.body.append(script);
  }));
  return scripts.get(src);
}

const PROVIDER_NAMES = { twitter: "X", threads: "Threads", instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook", youtube: "YouTube", vimeo: "Vimeo" };

function frameProvider(src) {
  try {
    const host = new URL(src).hostname;
    if (host.includes("youtube")) return "youtube";
    if (host.includes("vimeo")) return "vimeo";
    if (host.includes("tiktok")) return "tiktok";
    if (host.includes("facebook")) return "facebook";
  } catch { /* Unsupported frame. */ }
  return null;
}

function createFrame(src, provider) {
  const frame = document.createElement("iframe");
  frame.src = src;
  frame.title = (PROVIDER_NAMES[provider] || "Embedded") + " content";
  frame.loading = "lazy";
  frame.allowFullscreen = true;
  frame.style.cssText = "width:100%;max-width:100%;height:560px;border:0";
  return frame;
}

function renderSocial(node, { provider, url }) {
  const block = document.createElement("blockquote");
  const link = document.createElement("a");
  link.href = url;
  link.textContent = "View " + provider + " post";
  block.append(link);
  let frameUrl;
  if (provider === "twitter") { block.className = "twitter-tweet"; block.setAttribute("data-width", "550"); block.setAttribute("data-align", "center"); }
  if (provider === "threads") { block.className = "text-post-media"; block.setAttribute("data-text-post-permalink", url); }
  if (provider === "instagram") { block.className = "instagram-media"; block.setAttribute("data-instgrm-permalink", url); }
  if (provider === "tiktok") { block.className = "tiktok-embed"; block.setAttribute("cite", url); block.setAttribute("data-video-id", url.match(/\/video\/(\d+)/)[1]); block.append(document.createElement("section")); }
  if (provider === "facebook") frameUrl = "https://www.facebook.com/plugins/post.php?href=" + encodeURIComponent(url) + "&show_text=true&width=500";
  if (provider === "youtube") frameUrl = "https://www.youtube-nocookie.com/embed/" + new URL(url).searchParams.get("v");
  if (provider === "vimeo") frameUrl = "https://player.vimeo.com/video/" + new URL(url).pathname.slice(1);
  node.replaceChildren(block);
  if (frameUrl) node.prepend(createFrame(frameUrl, provider));
}

// Click-to-load card shown until the reader's consent allows embedded content.
function renderConsentCard(node, provider, url, onLoad, openPreferences) {
  const name = PROVIDER_NAMES[provider] || "external";
  const card = document.createElement("div");
  card.className = "article-embed-consent";
  const text = document.createElement("p");
  text.textContent = "This content is hosted by " + name + ". Loading it lets " + name + " set cookies and see that you visited this page.";
  const actions = document.createElement("div");
  const load = document.createElement("button");
  load.type = "button";
  load.textContent = "Load " + name + " content";
  load.addEventListener("click", onLoad);
  actions.append(load);
  if (openPreferences) {
    const settings = document.createElement("button");
    settings.type = "button";
    settings.className = "article-embed-consent-secondary";
    settings.textContent = "Privacy settings";
    settings.addEventListener("click", () => openPreferences());
    actions.append(settings);
  }
  if (url) {
    const link = document.createElement("a");
    link.href = url; link.target = "_blank"; link.rel = "noopener noreferrer";
    link.textContent = "Open on " + name;
    actions.append(link);
  }
  card.append(text, actions);
  node.replaceChildren(card);
}

export default function ArticleBody({ html, publicationSettings, className = "", ...props }) {
  const ref = useRef(null);
  const { state, analyticsCollectionEnabled: consentEnabled, openPreferences } = useConsent();
  const preferences = useRef(openPreferences);
  preferences.current = openPreferences;
  // Without a consent banner (or outside a publication, as in the editor preview) embeds load as before.
  const embedsAllowed = !consentEnabled || customCodeAllowed("FUNCTIONAL", state);
  const topicPrefix = buildPublicationUrl({ publication: { settings: publicationSettings }, path: "/" });
  const safe = useMemo(() => sanitizeArticleContent(html, { deferFrames: true }).replace(/href=(["'])\/topic\//g, (_, quote) => "href=" + quote + topicPrefix + "topic/"), [html, topicPrefix]);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const loadProviders = () => {
      const run = async (selector, src, process) => {
        if (!root.querySelector(selector)) return;
        try { await loadScript(src); if (root.isConnected) process?.(); } catch { /* Keep the source link available. */ }
      };
      void run(".twitter-tweet", "https://platform.twitter.com/widgets.js", () => window.twttr?.widgets?.load(root));
      void run(".instagram-media", "https://www.instagram.com/embed.js", () => window.instgrm?.Embeds?.process());
      // These providers scan their markup on script execution, including client navigation.
      for (const [selector, src] of [[".text-post-media", "https://www.threads.net/embed.js"], [".tiktok-embed", "https://www.tiktok.com/embed.js"]]) {
        if (root.querySelector(selector)) {
          const script = document.createElement("script"); script.src = src; script.async = true; root.append(script);
        }
      }
    };
    const openSettings = consentEnabled ? () => preferences.current?.() : null;
    let shown = false;
    const gate = (node, provider, url, render) => {
      if (node.dataset.rendered) return;
      if (embedsAllowed || node.dataset.loadRequested) { node.dataset.rendered = "true"; render(); shown = true; return; }
      renderConsentCard(node, provider, url, () => { node.dataset.loadRequested = "true"; node.dataset.rendered = "true"; render(); loadProviders(); }, openSettings);
    };
    root.querySelectorAll("[data-social-url]").forEach((node) => {
      const embed = socialEmbedUrl(node.getAttribute("data-social-url"));
      if (embed) gate(node, embed.provider, embed.url, () => renderSocial(node, embed));
    });
    root.querySelectorAll("[data-embed-frame]").forEach((node) => {
      const src = node.getAttribute("data-embed-frame");
      const provider = frameProvider(src);
      if (safeFrame(src)) gate(node, provider, null, () => node.replaceChildren(createFrame(src, provider)));
    });
    if (shown) loadProviders();
  }, [safe, embedsAllowed, consentEnabled]);
  return <div {...props} className={"article-rich-body " + className} ref={ref} dangerouslySetInnerHTML={{ __html: safe }} />;
}
